import * as Y from 'yjs';
import { useEffect, useState } from 'react';
import { LOCAL_ORIGIN, newId, type BoardDoc } from '../../doc/BoardDoc';
import { acquireBoard, releaseBoard } from '../../doc/docCache';
import type { Item } from '../../doc/schema';
import { api, ApiError } from '../../sync/api';
import { authStore, useAuth } from '../../sync/authStore';
import { resendUnsent } from '../../sync/uploadQueue';
import { deleteAssetLocally } from '../assets/localAssets';
import { assetIdsOf } from '../storage';

/**
 * The cutout drawer's document. An account's drawer is a hidden board on the server (see
 * Boards::drawerFor), so it syncs, stores assets and works offline exactly like a board; without
 * an account (the local scratch board, a share link) it is a local document of this browser.
 *
 * Inside the Y.Doc, next to the (unused) board maps:
 *   drawerFolders : Y.Map<id, Y.Map { name, order }>
 *   cutouts       : Y.Map<id, Y.Map { name, folderId, createdAt, w, h, count, kind, thumbAssetId }>
 *   cutoutItems   : Y.Map<id, Item[]>   the snapshot a cutout places, its assets cloned into the drawer
 * The payloads sit in their own map so listing the drawer never has to copy them.
 */

export interface DrawerFolder {
  name: string;
  order: number;
}

export interface DrawerCutout {
  name: string;
  folderId: string | null;
  createdAt: number;
  /** Size of the saved items' bounds, in board units. */
  w: number;
  h: number;
  /** How many items it holds, and the type of a lone one ('group' otherwise). */
  count: number;
  kind: string;
  /** Preview render, an asset of the drawer; null when rendering failed. */
  thumbAssetId: string | null;
}

export const LOCAL_DRAWER_ID = 'local-drawer';

export const foldersOf = (d: BoardDoc) => d.doc.getMap<Y.Map<unknown>>('drawerFolders');
export const cutoutsOf = (d: BoardDoc) => d.doc.getMap<Y.Map<unknown>>('cutouts');
const payloadsOf = (d: BoardDoc) => d.doc.getMap<Item[]>('cutoutItems');

// ---- which document ---------------------------------------------------------------

const cacheKey = (userId: string) => `vds:drawer:${userId}`;
const lookups = new Map<string, Promise<string | null>>();

/** The account's drawer board id: asked of the server once per session, remembered for offline use. */
function drawerIdFor(userId: string): Promise<string | null> {
  let p = lookups.get(userId);
  if (!p) {
    p = (async () => {
      try {
        const { boardId } = await api.get<{ boardId: string }>('/drawer');
        try {
          localStorage.setItem(cacheKey(userId), boardId);
        } catch {
          /* private mode */
        }
        return boardId;
      } catch (e) {
        // signed out meanwhile, or a server without the drawer yet: nothing to fall back on
        if (e instanceof ApiError) return null;
        try {
          return localStorage.getItem(cacheKey(userId));
        } catch {
          return null;
        }
      }
    })();
    lookups.set(userId, p);
    // a failed lookup is asked again next time
    void p.then((id) => {
      if (!id) lookups.delete(userId);
    });
  }
  return p;
}

/** Releasing waits a little, so edits made just before still reach IndexedDB and the server. */
const RELEASE_DELAY_MS = 20000;

/** Drawer documents whose unsent assets were queued again this session. */
const resent = new WeakSet<BoardDoc>();

/**
 * Opens the drawer of whoever is using the app. Null when an account's drawer cannot be found
 * (first use while offline). Call the returned `release` when done with it.
 */
export async function acquireDrawer(): Promise<{ drawer: BoardDoc; release: () => void } | null> {
  const user = authStore.getState().user;
  const id = user ? await drawerIdFor(user.id) : LOCAL_DRAWER_ID;
  if (!id) return null;
  const drawer = acquireBoard(id, user !== null);
  // one person's drawer, rarely changed on two devices at once: the slowest polling does
  drawer.provider?.setDefaultMode('saver');
  await drawer.whenReady;
  if (drawer.remote && !resent.has(drawer)) {
    resent.add(drawer);
    void resendUnsent(drawer.boardId, referencedAssets(drawer));
  }
  let released = false;
  return {
    drawer,
    release: () => {
      if (released) return;
      released = true;
      setTimeout(() => releaseBoard(drawer), RELEASE_DELAY_MS);
    },
  };
}

/** The drawer document while `enabled` (the panel is open); `missing` once it turned out unavailable. */
export function useDrawer(enabled: boolean): { drawer: BoardDoc | null; missing: boolean } {
  const userId = useAuth((s) => (s.status === 'loading' ? undefined : (s.user?.id ?? null)));
  const [state, setState] = useState<{ drawer: BoardDoc | null; missing: boolean }>({ drawer: null, missing: false });
  useEffect(() => {
    if (!enabled || userId === undefined) return;
    let cancelled = false;
    let held: { release: () => void } | null = null;
    void acquireDrawer().then((r) => {
      if (cancelled) {
        r?.release();
        return;
      }
      held = r;
      setState({ drawer: r?.drawer ?? null, missing: !r });
    });
    return () => {
      cancelled = true;
      held?.release();
      setState({ drawer: null, missing: false });
    };
  }, [enabled, userId]);
  return state;
}

// ---- folders ------------------------------------------------------------------------

export function addFolder(d: BoardDoc, name: string): string {
  const id = newId();
  let order = 0;
  foldersOf(d).forEach((y) => {
    const o = y.get('order');
    if (typeof o === 'number' && o >= order) order = o + 1;
  });
  d.doc.transact(() => {
    const y = new Y.Map<unknown>();
    y.set('name', name);
    y.set('order', order);
    foldersOf(d).set(id, y);
  }, LOCAL_ORIGIN);
  return id;
}

export function renameFolder(d: BoardDoc, id: string, name: string): void {
  d.doc.transact(() => foldersOf(d).get(id)?.set('name', name), LOCAL_ORIGIN);
}

/** Removes a folder; its cutouts stay, unsorted. */
export function deleteFolder(d: BoardDoc, id: string): void {
  d.doc.transact(() => {
    foldersOf(d).delete(id);
    cutoutsOf(d).forEach((y) => {
      if (y.get('folderId') === id) y.set('folderId', null);
    });
  }, LOCAL_ORIGIN);
}

// ---- cutouts ------------------------------------------------------------------------

export function addCutout(d: BoardDoc, meta: DrawerCutout, items: Item[]): string {
  const id = newId();
  d.doc.transact(() => {
    const y = new Y.Map<unknown>();
    for (const [k, v] of Object.entries(meta)) y.set(k, v);
    payloadsOf(d).set(id, items);
    cutoutsOf(d).set(id, y);
  }, LOCAL_ORIGIN);
  return id;
}

export function cutoutItems(d: BoardDoc, id: string): Item[] {
  const items = payloadsOf(d).get(id);
  return Array.isArray(items) ? structuredClone(items) : [];
}

export function renameCutout(d: BoardDoc, id: string, name: string): void {
  d.doc.transact(() => cutoutsOf(d).get(id)?.set('name', name), LOCAL_ORIGIN);
}

/** Files a cutout in a folder (null: unsorted). A folder that is gone meanwhile leaves it where it was. */
export function moveCutout(d: BoardDoc, id: string, folderId: string | null): void {
  if (folderId !== null && !foldersOf(d).has(folderId)) return;
  d.doc.transact(() => cutoutsOf(d).get(id)?.set('folderId', folderId), LOCAL_ORIGIN);
}

export function deleteCutout(d: BoardDoc, id: string): void {
  const mine = new Set(assetsOfCutout(d, id));
  d.doc.transact(() => {
    cutoutsOf(d).delete(id);
    payloadsOf(d).delete(id);
  }, LOCAL_ORIGIN);
  if (!mine.size) return;
  if (d.remote) {
    scheduleSweep(d);
    return;
  }
  // a local drawer: drop the files at once, unless another cutout shares them
  for (const used of referencedAssets(d)) mine.delete(used);
  for (const assetId of mine) void deleteAssetLocally(assetId);
}

// ---- assets -------------------------------------------------------------------------

function assetsOfCutout(d: BoardDoc, id: string): string[] {
  const out: string[] = [];
  const thumb = cutoutsOf(d).get(id)?.get('thumbAssetId');
  if (typeof thumb === 'string') out.push(thumb);
  const items = payloadsOf(d).get(id);
  if (Array.isArray(items)) for (const item of items) out.push(...assetIdsOf(item));
  return out;
}

/** Every asset the drawer still needs: what its cutouts place, and their previews. */
function referencedAssets(d: BoardDoc): Set<string> {
  const ids = new Set<string>();
  cutoutsOf(d).forEach((_y, id) => {
    for (const a of assetsOfCutout(d, id)) ids.add(a);
  });
  return ids;
}

const sweeps = new WeakMap<BoardDoc, ReturnType<typeof setTimeout>>();

/**
 * Deleted cutouts leave their files on the server; a while after the last deletion they are
 * swept (the server spares uploads younger than an hour). Only a fully synced document may say
 * what is unused: one still loading would not know the cutouts other devices added.
 */
function scheduleSweep(d: BoardDoc): void {
  clearTimeout(sweeps.get(d));
  sweeps.set(
    d,
    setTimeout(() => {
      const provider = d.provider;
      if (!provider || provider.store.getState().status !== 'synced') return;
      void api
        .post<{ removed: string[] }>(`/boards/${d.boardId}/assets/sweep`, { keep: [...referencedAssets(d)], apply: true })
        .then((r) => Promise.all(r.removed.map((id) => deleteAssetLocally(id))))
        .catch(() => undefined);
    }, 8000),
  );
}
